import { Blackboard, tagValue, untagValue } from "./Blackboard";
import type { ObjectEntity } from "./Interpreter";

const DEFAULT_CAPACITY = 8;
const DEFAULT_WEIGHT = 1;

export class InventoryManager {
  private _bb: Blackboard;
  private _objects: Record<string, ObjectEntity>;

  constructor(bb: Blackboard, objects: Record<string, ObjectEntity>) {
    this._bb = bb;
    this._objects = objects;
  }

  initPlacement(places: Record<string, { objects?: string[] }>): void {
    for (const [placeId, place] of Object.entries(places)) {
      for (const objId of place.objects ?? []) {
        const key = `_place_${objId}`;
        if (!this._bb.has(key)) {
          this._bb.set(key, tagValue(placeId));
        }
      }
    }
    if (!this._bb.has("_inventory")) {
      this._bb.set("_inventory", tagValue([]));
    }
    if (!this._bb.has("_inventory_capacity")) {
      this._bb.set("_inventory_capacity", tagValue(DEFAULT_CAPACITY));
    }
  }

  getIds(): string[] {
    const raw = untagValue(this._bb.get("_inventory"));
    return Array.isArray(raw) ? raw.map(String) : [];
  }

  setIds(ids: string[]): void {
    this._bb.set("_inventory", tagValue(ids));
  }

  has(objectId: string): boolean {
    return this.getIds().includes(objectId);
  }

  add(objectId: string): void {
    const ids = this.getIds();
    ids.push(objectId);
    this.setIds(ids);
    this._bb.set(`_place_${objectId}`, tagValue("_inventory"));
  }

  remove(objectId: string): string[] {
    const ids = this.getIds().filter((id) => id !== objectId);
    this.setIds(ids);
    return ids;
  }

  getCapacity(): { used: number; max: number } {
    const ids = this.getIds();
    let used = 0;
    for (const id of ids) {
      used += this.getItemWeight(id);
    }
    const max = this._bb.has("_inventory_capacity")
      ? Number(untagValue(this._bb.get("_inventory_capacity")))
      : DEFAULT_CAPACITY;
    return { used, max };
  }

  getItemWeight(entityId: string): number {
    return this._objects[entityId]?.weight ?? DEFAULT_WEIGHT;
  }

  getObjectsInRoom(roomId: string): string[] {
    const result: string[] = [];
    for (const objId of Object.keys(this._objects)) {
      const key = `_place_${objId}`;
      if (this._bb.has(key)) {
        const place = String(untagValue(this._bb.get(key)));
        if (place === roomId) result.push(objId);
      }
    }
    return result;
  }

  setPlace(objectId: string, placeId: string): void {
    this._bb.set(`_place_${objectId}`, tagValue(placeId));
  }
}
